import React, { useState, useEffect } from "react";
import { X, Tag as TagIcon } from "lucide-react";
import { supabase } from "../lib/supabase";

interface BlogTagsModalProps {
  isOpen: boolean;
  onClose: () => void;
  postId: string;
  onUpdate: () => void;
}

export default function BlogTagsModal({
  isOpen,
  onClose,
  postId,
  onUpdate,
}: BlogTagsModalProps) {
  const [tags, setTags] = useState<any[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      fetchTags();
    }
  }, [isOpen]);

  const fetchTags = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch all available tags
      const { data: allTags, error: tagsError } = await supabase
        .from("blog_tags")
        .select("*")
        .order("name");

      if (tagsError) throw tagsError;

      // Fetch current post's tags
      const { data: postTags, error: postTagsError } = await supabase
        .from("blog_tag_posts")
        .select("tag_id")
        .eq("post_id", postId);

      if (postTagsError) throw postTagsError;

      setTags(allTags || []);
      setSelectedTags(postTags?.map((pt) => pt.tag_id) || []);
    } catch (err) {
      console.error("Error fetching tags:", err);
      setError("Failed to load tags");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      // Delete existing tag mappings
      const { error: deleteError } = await supabase
        .from("blog_tag_posts")
        .delete()
        .eq("post_id", postId);

      if (deleteError) throw deleteError;

      // Insert new tag mappings
      if (selectedTags.length > 0) {
        const tagMappings = selectedTags.map((tagId) => ({
          post_id: postId,
          tag_id: tagId,
        }));

        const { error: insertError } = await supabase
          .from("blog_tag_posts")
          .insert(tagMappings);

        if (insertError) throw insertError;
      }

      onUpdate();
      onClose();
    } catch (err) {
      console.error("Error saving tags:", err);
      setError("Failed to save tags");
    } finally {
      setSaving(false);
    }
  };

  const toggleTag = (tagId: string) => {
    setSelectedTags((prev) =>
      prev.includes(tagId)
        ? prev.filter((id) => id !== tagId)
        : [...prev, tagId],
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <div className="flex items-center">
            <TagIcon className="h-5 w-5 text-gray-500 mr-2" />
            <h2 className="text-lg font-semibold text-gray-900">Manage Tags</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6">
          {error && (
            <div className="mb-4 bg-red-50 border-l-4 border-red-400 p-4">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {loading ? (
            <div className="flex justify-center items-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#F98B3D]"></div>
            </div>
          ) : (
            <div className="space-y-4">
              {tags.length === 0 ? (
                <p className="text-gray-500 text-center">No tags available</p>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  {tags.map((tag) => (
                    <label
                      key={tag.id}
                      className="flex items-center p-2 rounded border border-gray-200 hover:bg-gray-50 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedTags.includes(tag.id)}
                        onChange={() => toggleTag(tag.id)}
                        className="h-4 w-4 text-[#F98B3D] focus:ring-[#F98B3D] border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-900">
                        {tag.name}
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="mt-6 flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving || loading}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#F98B3D] hover:bg-[#e07a2c] disabled:opacity-50"
            >
              {saving ? "Saving..." : "Save Tags"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
